import { create } from 'zustand'
import type { ContactDetailResponse } from '../api/contacts'
import { getContactDetail } from '../api/contacts'

interface ContactDetailEntry {
  data: ContactDetailResponse | null
  loading: boolean
  error: string | null
  stationId: number | null
}

interface ContactsDetailState {
  byId: Record<number, ContactDetailEntry | undefined>
  fetch: (stationId: number, contactId: number) => Promise<ContactDetailResponse | null>
  setContact: (contactId: number, data: ContactDetailResponse, stationId?: number | null) => void
  setLoading: (contactId: number, loading: boolean) => void
  setError: (contactId: number, error: string | null) => void
  invalidate: (contactId: number) => void
  lastMergeEvent: { fromContactId: number; toContactId: number } | null
  lastIdentityReassignedEvent: {
    identityId: number
    fromContactId: number
    toContactId: number
  } | null
  setLastMergeEvent: (event: { fromContactId: number; toContactId: number } | null) => void
  setLastIdentityReassignedEvent: (
    event: { identityId: number; fromContactId: number; toContactId: number } | null,
  ) => void
  clear: () => void
}

const useContactsDetailStore = create<ContactsDetailState>((set, get) => ({
  byId: {},
  async fetch(stationId, contactId) {
    if (!stationId) {
      return null
    }

    const existing = get().byId[contactId]
    if (existing?.loading) {
      return existing.data ?? null
    }

    set((state) => ({
      byId: {
        ...state.byId,
        [contactId]: {
          data: existing?.data ?? null,
          loading: true,
          error: null,
          stationId: stationId ?? existing?.stationId ?? null,
        },
      },
    }))

    try {
      const data = await getContactDetail(stationId, contactId)
      set((state) => ({
        byId: {
          ...state.byId,
          [contactId]: {
            data,
            loading: false,
            error: null,
            stationId: stationId ?? existing?.stationId ?? null,
          },
        },
      }))
      return data
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load contact.'
      set((state) => ({
        byId: {
          ...state.byId,
          [contactId]: {
            data: existing?.data ?? null,
            loading: false,
            error: message,
            stationId: stationId ?? existing?.stationId ?? null,
          },
        },
      }))
      return existing?.data ?? null
    }
  },
  setContact(contactId, data, stationId = null) {
    set((state) => ({
      byId: {
        ...state.byId,
        [contactId]: {
          data,
          loading: false,
          error: null,
          stationId: stationId ?? state.byId[contactId]?.stationId ?? null,
        },
      },
    }))
  },
  setLoading(contactId, loading) {
    set((state) => ({
      byId: {
        ...state.byId,
        [contactId]: {
          data: state.byId[contactId]?.data ?? null,
          loading,
          error: state.byId[contactId]?.error ?? null,
          stationId: state.byId[contactId]?.stationId ?? null,
        },
      },
    }))
  },
  setError(contactId, error) {
    set((state) => ({
      byId: {
        ...state.byId,
        [contactId]: {
          data: state.byId[contactId]?.data ?? null,
          loading: false,
          error,
          stationId: state.byId[contactId]?.stationId ?? null,
        },
      },
    }))
  },
  invalidate(contactId) {
    set((state) => {
      const next = { ...state.byId }
      delete next[contactId]
      return { byId: next }
    })
  },
  lastMergeEvent: null,
  lastIdentityReassignedEvent: null,
  setLastMergeEvent(event) {
    set({ lastMergeEvent: event })
  },
  setLastIdentityReassignedEvent(event) {
    set({ lastIdentityReassignedEvent: event })
  },
  clear() {
    set({ byId: {}, lastMergeEvent: null, lastIdentityReassignedEvent: null })
  },
}))

export default useContactsDetailStore
